/**
 * The header user menu — **the missing REGION**, not a missing control.
 *
 * > *"I do not see a way for me to setup a passkey. I think we need the whole header from DP, i.e.
 * > the user dropdown in the header with some of the settings under that."*
 *
 * Before this, the console had no user menu at all, so every per-user affordance had nowhere to
 * live. That is why the report reads as *"no passkey"*.
 *
 * ⭐ PORTED FROM `cloud/dp/shell/src/components/Header.tsx:295-367` (its `profile` dropdown), plus
 * `useDropdown.ts` for the open/close behaviour. ⛔ Read, never imported: `cloud/dp/shell` is a
 * different package. Only the STRUCTURE and the INTERACTION are ported — the styling is this
 * package's own tokens and scale, and every endpoint was re-derived from `cp-auth-api`.
 *
 * # ⛔ THE DP MENU, ITEM BY ITEM — AND WHAT THIS ONE DOES WITH EACH
 *
 * A CP platform operator is a different principal from a DP org user, so the sets legitimately
 * differ. Every difference below is a decision, not an omission.
 *
 * | DP item (`Header.tsx`) | Here | Why |
 * |---|---|---|
 * | avatar image (`user.googleAvatarUrl`) | ✅ **KEPT as of WS-4 G5 / B26** | ⚠️ This row read **dropped — "no producer"** until 2026-08-08, and that was true when written: the access token carries `sub`, `email`, `siteRoles`, `sitePermissions` and nothing else (`platform/auth/useAuth.ts:106-113`). G5 built the producer — `cp_auth.operators.avatar_url` (migration `0028`), captured from Google's `picture` claim through `sanitizeAvatarUrl` (https + host allow-list + length + no embedded credentials), served by `GET /v1/auth/me`, read here via `useOperatorProfile()`. ⛔ {@link emailInitials} is **still the fallback and still the common case** (a github login and an account with no photo both yield `null`), so the monogram is not dead code — it is the answer for every operator we hold no photo for. |
 * | `user.fullName` | ⛔ **dropped** | Same reason: no name claim. The email IS the identity here, and it is what the operator recognises in an audit log. |
 * | role badges (`orgRoles`) | ✅ **kept**, as `siteRoles` with no active role; with an active role, as the role chip and the role segments | The CP analogue. For a platform operator this is the single most load-bearing fact about themselves — it decides every section they can see — so it is worth the two lines. |
 * | `ChannelSwitcher` (per-user release channel) | ⛔ **dropped** | It has no CP counterpart and should not be invented. DP's reads `GET /v1/release/my-channels` on DP `core-api` — a per-user pick among the channels an ORG is entitled to CONSUME. A platform operator does not consume a release channel; they author and promote them, on the Releases screens. There is no *"my channel"* for an operator to switch, so a control here would be a picker over an empty set. |
 * | `Profile` → `/admin/users` | ⛔ **dropped** | There is no operator self-profile screen in this console. The one thing that surface would have carried — credentials — is the Passkeys item below. |
 * | `Settings` → `/settings/general` | ⛔ **dropped as a destination**, contents redistributed | ⚠️ **CORRECTED (R-27): this cell used to open *"This console has no settings section."* — which was true when written and is now FALSE.** There has been a `settings` section since R9 N-1: `ShellSection` includes `'settings'`, and it holds six platform entries, `settings-platform` first (Grafana moved to Investigate in plan 2026-10-14-console-nav-rail). ⇒ **the DECISION is unchanged and the reason is the real one: DP's `/settings/general` is not ported because its four contents were REDISTRIBUTED** — **General** (appearance) → the header's `PolarityToggle`; **Passkeys** → the item below; **Connected Apps** → reported, not built (see the gap note); **About** (versions) → not a per-user setting, and the console already surfaces platform versions on its own screens. ⛔ None of those four is a per-user preference page, which is why the CP settings section that DOES exist is not the destination either — it holds PLATFORM settings, not the signed-in operator's. |
 * | `Sign out` | ✅ **kept**, and MOVED here | It was a top-level header button. In the DP header it lives in this menu, and a menu that carries the identity but not the way out of it is the odd one. ⛔ Its `data-testid` is unchanged (`section-header-sign-out`) so the five existing suites that drive sign-out keep naming the same control. |
 *
 * # ⛔ THE THEME CONTROL IS NOT IN THIS MENU, AND THAT IS THE OWNER'S OWN RULING
 *
 * Platform-11 reads, verbatim: *"No dark-mode toggle. **Add the widget the DP shell already has in
 * its header.**"* The DP shell puts `PolarityToggle` in the header's right-hand cluster **outside**
 * the profile menu (`Header.tsx:291-293`); DP's menu carries no theme control at all. This console
 * now does the same, and `theme/PolarityToggle.tsx` records the second reason it must stay out:
 * `AuthenticatedShell.frame()` passes **no session** on every zero-access path, so a theme control
 * nested inside a session-gated menu would be unreachable for precisely the population that has
 * nothing else on screen. ⇒ One theme control, in the header, ungated. Duplicating it in here would
 * be two visible controls for one setting, three centimetres apart.
 *
 * # ⚠️ A KNOWN GAP, REPORTED RATHER THAN PAPERED OVER
 *
 * `cp-auth-api` has a real per-operator OAuth-grant surface — `GET /v1/auth/oauth2/grants`, cited in
 * `passkey.controller.ts:64` — which is the CP counterpart of DP's **Connected Apps**. Revoking a
 * delegated grant belongs in a user menu. It is not built here because it is a screen rather than a
 * menu row, and inventing one silently is how a half-surface ships. Reported to the lead.
 */
import { useEffect, useRef, useState } from 'react';
import { ChevronDown, KeyRound, Loader2, LogOut, RotateCw, ShieldCheck } from 'lucide-react';
import { Badge } from '@witness-ai/ward-design-system';
import { PLATFORM_ADMIN_ROLE } from '@zion/sdk-contracts';
import { usePermissionSnapshot } from '@zion/sdk-react';
import { useOperatorProfile } from './profile-context';
import { focusPanel } from '../panes/dialog-panel';
import { PasskeyDialog } from './PasskeyDialog';
import { Button } from './Button';
import { roleFullName, roleName } from './role-names';
import {
  actingAsText,
  clockOf,
  elevationName,
  useActiveRole,
  type ActiveRoleValue,
  type RoleSwitchResult,
} from './active-role-context';

/**
 * The role a session elevates to, which the role segments list last with its key icon:
 * `PLATFORM_ADMIN_ROLE` of the contract, which holds every site permission.
 */
export const ELEVATED_ROLE = PLATFORM_ADMIN_ROLE;

/**
 * The screen-reader hint of the elevated role's segment while it is not active. The key icon says
 * it visually. It names no length: the elevation length is a CP setting (K7).
 */
export const ELEVATION_HINT = 'Needs passkey';

/**
 * Whether a switch to `role` can ask for a passkey. The server answers an elevation for every role
 * that holds a permission the active role lacks (D5), and the console holds no role catalog. So
 * only a return to the rest role is sure to need none (R-003). With no rest role in the token,
 * `platform_admin` alone counts.
 */
export function mayNeedPasskey(value: Pick<ActiveRoleValue, 'restRole'>, role: string): boolean {
  return value.restRole === null ? role === ELEVATED_ROLE : role !== value.restRole;
}

/**
 * The options of the "Act as" group: the held roles in slug order with `platform_admin` last. There is no "App access only" option: a person who holds `platform_admin`
 * alone rests as `platform_admin` (K2).
 */
export function actAsOptions(held: readonly string[]): readonly string[] {
  return [...held].sort((a, b) => (a === ELEVATED_ROLE ? 1 : b === ELEVATED_ROLE ? -1 : a.localeCompare(b)));
}

/** The status sentence of one finished switch (direction 5, the state table). */
export function switchStatusText(result: RoleSwitchResult, chosen: string | null, before: string | null): string {
  switch (result.kind) {
    case 'switched':
      return `Acting as ${actingAsText(chosen)}.`;
    case 'cancelled':
      return `Still acting as ${actingAsText(before)}. Elevation cancelled.`;
    case 'not-verified':
      return `Still acting as ${actingAsText(before)}. The passkey did not verify.`;
    case 'failed':
      return `The switch did not complete. Still acting as ${actingAsText(before)}. ${result.message}`;
  }
}

/** The status sentence of one finished renew of `role` (K8). A renew that ends keeps the old end. */
export function renewStatusText(result: RoleSwitchResult, role: string | null): string {
  switch (result.kind) {
    case 'switched':
      return `${elevationName(role)} renewed.`;
    case 'cancelled':
      return `${elevationName(role)} was not renewed. Renew cancelled.`;
    case 'not-verified':
      return `${elevationName(role)} was not renewed. The passkey did not verify.`;
    case 'failed':
      return `The renew did not complete. ${elevationName(role)} was not renewed. ${result.message}`;
  }
}

const MENU_ID = 'user-menu';

/**
 * Two initials for the avatar chip, derived from the email local part.
 *
 * `dave.brewster@witness.ai` → `DB`; `ops@witness.ai` → `OP`; `a@witness.ai` → `A`.
 *
 * ⛔ `split('@', 1).join('')` rather than `split('@')[0] ?? ''` — the index form is `string |
 * undefined` under a stricter compiler and the `??` arm is unreachable for any string, so it would be
 * a branch no test can enter. This form is total by construction.
 *
 * ⚠️ Returns `'?'` for an address with no usable local part (`@witness.ai`). Reachable, therefore
 * tested — and honest: a blank chip reads as a rendering fault, and inventing a letter would name the
 * operator something they are not.
 */
export function emailInitials(email: string): string {
  const local = email.split('@', 1).join('');
  const words = local.split(/[^a-zA-Z0-9]+/).filter((word) => word !== '');
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return `${words[0][0]}${words[1][0]}`.toUpperCase();
}

/** Wrap an index into `[0, count)`, for both directions. `((n % c) + c) % c` handles `-1`. */
export function wrapIndex(next: number, count: number): number {
  return ((next % count) + count) % count;
}

/** The menu's items, in document order. `null` container ⇒ empty, so callers need no guard. */
export function menuItemsIn(menu: HTMLElement | null): readonly HTMLElement[] {
  if (menu === null) return [];
  return [...menu.querySelectorAll<HTMLElement>('[role="menuitem"], [role="menuitemradio"]')];
}

/**
 * Focus the item at `index` (wrapped) and return the index actually focused.
 *
 * ⛔ Returns a plain `number` rather than `number | null` so the caller can write
 * `setRoving(focusMenuItem(...))` with no branch of its own. An empty menu answers `0`, which is
 * where a freshly-opened menu starts anyway — and both arms are covered by direct unit tests rather
 * than by a component state that cannot occur.
 */
export function focusMenuItem(menu: HTMLElement | null, index: number): number {
  const items = menuItemsIn(menu);
  if (items.length === 0) return 0;
  const target = wrapIndex(index, items.length);
  items[target].focus();
  return target;
}

export type MenuKeyAction = 'next' | 'prev' | 'first' | 'last' | 'close' | 'none';

/**
 * Classify a keypress inside the open menu.
 *
 * ⚠️ **`Tab` is treated exactly like `Escape`** — close, and put focus back on the trigger. The
 * WAI-ARIA menu-button pattern says close and move focus to the next element after the button; that
 * is indistinguishable to the operator (one more Tab gets them there) and it is deterministic,
 * whereas "let the default Tab run from a node React is about to unmount" is not. Either way the menu
 * cannot be Tabbed *into the void*, which is the failure that matters.
 */
export function classifyMenuKey(key: string): MenuKeyAction {
  switch (key) {
    case 'ArrowDown':
      return 'next';
    case 'ArrowUp':
      return 'prev';
    case 'Home':
      return 'first';
    case 'End':
      return 'last';
    case 'Escape':
    case 'Tab':
      return 'close';
    default:
      return 'none';
  }
}

/**
 * The menu index that ArrowRight or ArrowLeft moves to from `focused`, or -1. The side arrows move
 * only among the role segments, which sit in one row, and only from a segment; they wrap in the row.
 */
export function segmentStepIndex(items: readonly HTMLElement[], focused: Element | null, key: string): number {
  const step = key === 'ArrowRight' ? 1 : key === 'ArrowLeft' ? -1 : 0;
  const segments = items.filter((item) => item.getAttribute('role') === 'menuitemradio');
  const at = segments.findIndex((segment) => segment === focused);
  if (step === 0 || at === -1) return -1;
  return items.indexOf(segments[wrapIndex(at + step, segments.length)]);
}

/**
 * ⭐⭐ **APG CHARACTER TYPE-AHEAD — the standard way a `role="menu"` scales.**
 *
 * WAI-ARIA's menu pattern specifies type-ahead: a printable character moves focus to the next item
 * whose label starts with it, for ~15 lines in the arithmetic all three menus share. A long list also
 * gets a filter field through `ActionMenu`'s `search` prop, as the app picker does. That field sits
 * beside the `role="menu"` element, because an `<input>` is not a valid child of a menu.
 *
 * ⛔ **DELIBERATELY NOT AN ARM OF {@link classifyMenuKey}, and that is not squeamishness.** Two
 * reasons, both load-bearing:
 *  1. `classifyMenuKey` answers *"which navigation action"* over a CLOSED set of named keys; this
 *     answers *"is this a search character"* over the open set of everything else. Folding them makes
 *     the return type `MenuKeyAction | string` — a union whose second arm is unbounded.
 *  2. `classifyMenuKey('a') === 'none'` is a **pinned assertion** in `user-menu.test.tsx`. A change to an
 *     existing arm for a new feature can break it with no signal.
 *
 * ⚠️ **Single characters only.** `'Enter'`, `'ArrowLeft'`, `'F3'` are all length > 1, so the length
 * test excludes every named key without enumerating them — which is what makes this total against a
 * key set the browser, not this file, decides. Space is excluded on purpose: it activates the focused
 * menuitem, and swallowing it to start a search would break the item the operator is standing on.
 */
export function isTypeAheadKey(key: string): boolean {
  return key.length === 1 && key !== ' ';
}

/**
 * The attribute an item opts into type-ahead with. @see typeAheadIndex
 *
 * ⛔ **AN EXPLICIT ATTRIBUTE, NOT `textContent`, and the first cut used `textContent` — three reasons
 * it was wrong:**
 *  1. ⛔ **`textContent` reads `sr-only` prose.** This package has already been bitten by that
 *     (`toHaveTextContent` matching hidden text), so a screen-reader-only hint would silently become
 *     part of what the operator's keystroke is matched against.
 *  2. ⚠️ **It concatenates.** The picker's rows render a name and a slug as sibling spans, so the
 *     scraped string is `"Perf Labperf"` — searchable by accident rather than by decision.
 *  3. ⭐ **Opt-in is what keeps this change from reaching menus nobody re-tested.** `ActionMenu`'s
 *     existing caller (the data-plane action menu) sets no `data-typeahead`, so every one of its items
 *     is skipped and its keyboard behaviour is byte-for-byte unchanged. A `textContent` scrape would
 *     have silently given two shipped menus a new behaviour on the same commit.
 */
export const TYPEAHEAD_ATTR = 'data-typeahead';

/**
 * The index of the item whose {@link TYPEAHEAD_ATTR} starts with `text`, the characters the
 * operator typed in quick succession (D9 of the G2 cleanup plan). Case-insensitive.
 *
 * A run of one letter cycles, from `from + 1`. `"b"` and `"bb"` both search for `b` after the
 * focused item, so pressing the same letter again advances to the next match. That is how an
 * operator reaches the second of two apps beginning with "w". Starting at `from` would re-find the
 * item already focused and the key would appear dead.
 *
 * A longer text searches from `from` itself. Typing `dp-b` keeps the focus on `dp-beta` while
 * each letter narrows the match, so names that share a prefix stay reachable. With nothing focused
 * (`from` is -1), the search starts at the first item.
 *
 * ⚠️ Returns `-1` when nothing matches, so the caller can leave focus **exactly where it is**. Moving
 * focus to item 0 on a miss is the plausible-looking wrong behaviour: a typo would silently relocate
 * the operator to the top of a list they had scrolled into.
 *
 * ⚠️ An item with no attribute is SKIPPED, not matched against the empty string — otherwise every
 * opted-out item would match every keystroke, which is the inverse of opt-in.
 */
export function typeAheadIndex(
  items: readonly HTMLElement[],
  text: string,
  from: number,
): number {
  const typed = text.toLowerCase();
  const cycles = [...typed].every((char) => char === typed.charAt(0));
  const needle = cycles ? typed.charAt(0) : typed;
  const start = cycles ? from + 1 : Math.max(from, 0);
  for (let step = 0; step < items.length; step++) {
    const index = wrapIndex(start + step, items.length);
    const label = items[index].getAttribute(TYPEAHEAD_ATTR);
    if (label !== null && label.toLowerCase().startsWith(needle)) return index;
  }
  return -1;
}

/**
 * Whether a pointer landed outside the menu's container.
 *
 * ⛔ A `null` container answers `false` — *"not known to be outside"*. The inverse default would
 * close the menu on the very click that opened it during the commit where the ref is not yet
 * attached.
 */
export function isOutside(container: HTMLElement | null, target: EventTarget | null): boolean {
  if (container === null) return false;
  return !(target instanceof Node) || !container.contains(target);
}

export interface UserMenuProps {
  /** The signed-in operator's email, or `null` when the token carried none. */
  readonly email: string | null;
  readonly onSignOut: () => void;
}

export function UserMenu({ email, onSignOut }: UserMenuProps): React.JSX.Element {
  /**
   * ⚠️ Read from CONTEXT, and that does NOT contradict `SectionHeader`'s rule about `useAuth()`.
   * That rule exists because `useAuth` is `useState`-backed rather than a context, so calling it
   * twice creates two independent session state machines. `usePermissionSnapshot` IS a context, fed
   * once by `CpAdminApp`, and reading it is its intended use.
   *
   * ⚠️ Its default is `EMPTY_PERMISSION_SNAPSHOT`, so outside a provider `siteRoles` is `[]` and NO
   * badges render. That is honest: we make no claim rather than a wrong one.
   */
  const { siteRoles } = usePermissionSnapshot();

  /**
   * The active role of the session (plan 2026-10-22-cp-active-role), or `null` when no provider
   * gives one: outside `CpAdminApp`, or a token that carries no `heldSiteRoles` claim. With `null`
   * the menu lists `siteRoles` as it did before the active role existed.
   */
  const active = useActiveRole();

  /**
   * B26 / G5 — the operator's own photo, or `null`.
   *
   * ⛔ **Same context argument as `siteRoles` above, and the default is the same kind of honest.**
   * `useOperatorProfile()` returns `null` outside a provider (unlike `useAppsContext`, which throws —
   * `profile-context.tsx` records why the two differ), so the five suites that mount this menu without
   * one keep rendering the monogram instead of crashing or issuing a real request.
   *
   * ⚠️ `null` covers three causes — not yet arrived, the read failed, no operator row — and all three
   * render the monogram deliberately. There is nothing to distinguish for a decorative image, and a
   * spinner in a 24px chip would be worse than the initials it replaced.
   */
  const profile = useOperatorProfile();
  const avatarUrl = profile?.avatarUrl ?? null;

  const [open, setOpen] = useState(false);
  const [roving, setRoving] = useState(0);
  const [passkeysOpen, setPasskeysOpen] = useState(false);
  /** The role a switch is moving to, while it runs (`undefined`: no switch runs; `null`: a renew of no role). */
  const [switchingTo, setSwitchingTo] = useState<string | null | undefined>(undefined);
  /*
   * The sentence of the last transition, for the one polite status region (direction 6.3), with the
   * active role it describes. A role change from elsewhere (an expiry, another tab) retires it.
   */
  const [statusRecord, setStatusRecord] = useState<{ readonly text: string; readonly role: string | null } | null>(null);
  const status = statusRecord !== null && active !== null && statusRecord.role === active.activeRole ? statusRecord.text : null;

  const containerRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  /**
   * ⛔ Suppresses the focus-return for the ONE close that must not take it: opening the passkey
   * dialog. That close and the dialog's mount happen in the same commit, and child effects run
   * before the parent's — so without this the dialog would focus its card and this component would
   * immediately yank focus back to the trigger, leaving a modal open with focus behind it.
   */
  const restoreOnClose = useRef(true);
  /* Set by a role menu request: the menu then opens with the focus on the active segment. */
  const focusActiveOnOpen = useRef(false);

  /**
   * Focus management across the open↔close transition.
   *
   * ⛔ GATED ON A REAL TRANSITION (`prevOpen`), never on `open` alone. An effect that focuses the
   * trigger whenever `open` is false fires ON MOUNT — stealing focus from whatever the operator was
   * doing and, in a scrolled container, jumping the page. This codebase has shipped that defect
   * before; the ref is what makes "closed" and "just closed" different states.
   */
  const prevOpen = useRef(false);
  useEffect(() => {
    const wasOpen = prevOpen.current;
    prevOpen.current = open;
    if (open && !wasOpen) {
      setRoving(focusActiveOnOpen.current ? focusActiveSegment() : focusMenuItem(menuRef.current, 0));
      focusActiveOnOpen.current = false;
      return;
    }
    if (!open && wasOpen) {
      // A closed menu keeps no roving stop, so a stale stop cannot move the focus of the next open.
      setRoving(0);
      if (restoreOnClose.current) focusPanel(triggerRef.current);
      restoreOnClose.current = true;
    }
  }, [open]);

  /* Focus the checked segment (the first item when none is checked) and return its index. */
  function focusActiveSegment(): number {
    const checked = menuItemsIn(menuRef.current).findIndex((item) => item.getAttribute('aria-checked') === 'true');
    return focusMenuItem(menuRef.current, Math.max(checked, 0));
  }

  /*
   * 344: rows of the open menu can leave (the Renew row at the end of an elevation). The roving stop
   * then stays on a row that exists, and a focus that left with its row returns to that stop.
   * The row set follows the active role.
   */
  useEffect(() => {
    if (!open) return;
    const items = menuItemsIn(menuRef.current);
    const focused = document.activeElement;
    // A focused row that moved up keeps the stop: the stop follows it to its new index.
    const at = items.findIndex((item) => item === focused);
    if (at !== -1) {
      if (at !== roving) setRoving(at);
      return;
    }
    const lost = focused === null || focused === document.body || !focused.isConnected;
    if (roving >= items.length || lost) setRoving(focusMenuItem(menuRef.current, Math.min(roving, items.length - 1)));
  }, [open, roving, active]);

  /* Open the menu at the operator's request: the last outcome is then old, so it leaves. */
  function openByOperator(): void {
    setStatusRecord(null);
    setOpen(true);
  }

  /** Outside pointer closes. ⛔ `mousedown`, not `click` — a menu that survives until mouseup reads as stuck. */
  useEffect(() => {
    if (!open) return undefined;
    const onDocumentMouseDown = (event: MouseEvent): void => {
      if (isOutside(containerRef.current, event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocumentMouseDown);
    return () => document.removeEventListener('mousedown', onDocumentMouseDown);
  }, [open]);

  /*
   * The end of the elevation. The ended elevation speaks in the notice slot of `ElevationNotice`,
   * not in this menu's status region (direction e, change 1, rule 6).
   */
  const elevatedUntilMs = active?.elevatedUntilMs ?? null;

  // "Switch role" on the 403 panel opens this menu with the focus on the active segment.
  const menuRequest = active?.menuRequest ?? 0;
  const lastMenuRequest = useRef(menuRequest);
  useEffect(() => {
    if (menuRequest === lastMenuRequest.current) return;
    lastMenuRequest.current = menuRequest;
    setStatusRecord(null);
    // An open menu takes the focus now; a later open by the operator then starts at the first item.
    focusActiveOnOpen.current = !open;
    if (open) setRoving(focusActiveSegment());
    else setOpen(true);
  }, [menuRequest, open]);

  function onMenuKeyDown(event: React.KeyboardEvent<HTMLDivElement>): void {
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      // A modified side arrow is a browser or system shortcut (Alt+ArrowLeft is Back), so it passes.
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      event.preventDefault();
      const side = segmentStepIndex(menuItemsIn(menuRef.current), document.activeElement, event.key);
      if (side !== -1) setRoving(focusMenuItem(menuRef.current, side));
      return;
    }
    const action = classifyMenuKey(event.key);
    if (action === 'none') return;
    event.preventDefault();
    if (action === 'close') {
      setOpen(false);
      return;
    }
    const items = menuItemsIn(menuRef.current);
    const target =
      action === 'next'
        ? roving + 1
        : action === 'prev'
          ? roving - 1
          : action === 'first'
            ? 0
            : items.length - 1;
    setRoving(focusMenuItem(menuRef.current, target));
  }

  function openPasskeys(): void {
    restoreOnClose.current = false;
    setOpen(false);
    setPasskeysOpen(true);
  }

  /**
   * Renew the elevation (K8). The menu closes first, because the passkey dialog opens next and the
   * focus returns to the trigger after it. The status region names the outcome.
   */
  function renew(value: ActiveRoleValue): void {
    if (switchingTo !== undefined) return;
    const role = value.activeRole;
    // The renew is in flight until it answers, also after the menu closed for the passkey dialog.
    setSwitchingTo(role);
    setStatusRecord({ text: `Renewing ${elevationName(role)}…`, role });
    setOpen(false);
    void value.renewElevation().then((result) => {
      setSwitchingTo(undefined);
      setStatusRecord({ text: renewStatusText(result, role), role });
      if (result.kind === 'switched') return;
      // The visible status is in the menu, so a renew that did not complete opens it again.
      showOutcome();
    });
  }

  /* Open the menu on a switch outcome that did not complete, so a sighted operator reads it there. */
  function showOutcome(): void {
    restoreOnClose.current = true;
    setOpen(true);
  }

  /** ⛔ Focus is returned HERE, from an event — never from an effect. See `PasskeyDialogProps.onClose`. */
  function closePasskeys(): void {
    setPasskeysOpen(false);
    focusPanel(triggerRef.current);
  }

  /**
   * Switch to `role`. A switch that can need a passkey ({@link mayNeedPasskey}) closes the menu
   * first, because the passkey dialog opens next and focus returns to the trigger after it
   * (direction 5). Any other switch keeps the menu open with "Switching…" on the chosen item. The
   * menu closes after a completed switch, and stays open to show the outcome of any other result.
   */
  function choose(value: ActiveRoleValue, role: string): void {
    // One switch at a time: the other rows read aria-disabled while it runs, and a press does nothing.
    if (switchingTo !== undefined || role === value.activeRole) return;
    const before = value.activeRole;
    // One switch in flight at a time, also after the menu closed for the passkey dialog.
    setSwitchingTo(role);
    setStatusRecord({ text: `Switching to ${actingAsText(role)}…`, role: before });
    if (mayNeedPasskey(value, role)) setOpen(false);
    void value.switchRole(role).then((result) => {
      setSwitchingTo(undefined);
      const text = switchStatusText(result, role, before);
      if (result.kind === 'switched') {
        setStatusRecord({ text, role });
        setOpen(false);
        return;
      }
      setStatusRecord({ text, role: before });
      // The visible status is in the menu, so a switch that did not complete keeps it open.
      showOutcome();
    });
  }

  const label = email ?? 'Account';
  const elevated = active !== null && elevatedUntilMs !== null;
  const options = active === null ? [] : actAsOptions(active.heldSiteRoles);
  // The segments show while a held role is not active, also for one held role with none active.
  const showSegments = active !== null && options.some((role) => role !== active.activeRole);
  const triggerName =
    active === null
      ? undefined
      : `Account, ${label}, acting as ${actingAsText(active.activeRole)}${
          elevatedUntilMs === null ? '' : `, elevated until ${clockOf(elevatedUntilMs)}`
        }`;

  /* The menu items in document order, so each gets its roving index. */
  let itemIndex = 0;
  const nextIndex = (): number => itemIndex++;

  return (
    <div ref={containerRef} className="relative" data-testid="section-header-session">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => {
          if (open) setOpen(false);
          else openByOperator();
        }}
        onKeyDown={(event) => {
          /*
            ArrowDown opens the menu with focus already on the first item — the keyboard idiom for a
            menu button. Enter and Space are the button's own native activation and need no code.
          */
          if (event.key !== 'ArrowDown') return;
          event.preventDefault();
          openByOperator();
        }}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={triggerName}
        /* ⛔ Only while the menu exists: `aria-controls` pointing at an absent id is a broken reference. */
        aria-controls={open ? MENU_ID : undefined}
        data-testid="user-menu-trigger"
        className="flex items-center gap-2 rounded-lg border border-transparent px-2 py-1 text-sm transition-colors hover:bg-surface-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring"
      >
        {/*
          ⛔ `aria-hidden` — the initials are a visual shorthand for the email that follows, so
          announcing "D B, dave.brewster@witness.ai" would read the identity twice. With no active
          role the button's accessible name comes from the text; with one, `aria-label` names the role.
        */}
        {avatarUrl === null ? (
          <span
            aria-hidden="true"
            data-testid="user-menu-monogram"
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent/15 text-2xs font-bold tracking-tight text-accent"
          >
            {email === null ? '—' : emailInitials(email)}
          </span>
        ) : (
          /*
           * ⛔⛔ `referrerPolicy="no-referrer"` IS NOT OPTIONAL, AND IT IS NOT THE SAME QUESTION THE
           * PRODUCER ANSWERED. `sanitizeAvatarUrl` validated the URL at the WRITE boundary — https, an
           * allow-listed Google host, no embedded credentials. None of that stops the BROWSER attaching
           * this console's own URL as a `Referer` on every avatar fetch, which would hand Google the
           * internal admin path the operator is on. The column being trustworthy and the sink being
           * safe are two different properties.
           *
           * ⛔ `aria-hidden`, `alt=""`, and NOT in the tab order: the image is a visual shorthand for
           * the email rendered immediately after it. An `alt` of the operator's name would announce the
           * identity twice — the same reasoning the monogram carries.
           *
           * ⚠️ NO `onError` fallback to the monogram, deliberately. A broken photo is a cosmetic
           * degradation, and swapping the chip on a load failure means the identity control changes
           * shape milliseconds after paint — motion that says "something is wrong" about the operator's
           * own identity when nothing is.
           */
          <img
            src={avatarUrl}
            alt=""
            aria-hidden="true"
            referrerPolicy="no-referrer"
            data-testid="user-menu-avatar"
            className="h-6 w-6 shrink-0 rounded-full bg-accent/15 object-cover"
          />
        )}
        {/*
          ⛔ The email is ALWAYS VISIBLE, not `hidden sm:inline` as in the DP original. This console is
          a wide-monitor operator tool with a 240px rail; hiding the identity would leave a trigger
          whose only accessible name is an aria-hidden glyph. `section-header-email` is kept as the
          testid — and is absent when the token carried no email, which the existing chrome suite
          asserts.
        */}
        {email === null ? (
          <span className="text-secondary">{label}</span>
        ) : (
          <span
            data-testid="section-header-email"
            title={email}
            className="max-w-[16rem] truncate text-secondary"
          >
            {email}
          </span>
        )}
        {/* The active role, on every page without a click: the accent chip with a shield while elevated (K8). */}
        {active === null || active.activeRole === null ? null : (
          <RoleChip role={active.activeRole} elevated={elevated} testId="user-menu-acting-as" shieldTestId="user-menu-elevated" />
        )}
        <ChevronDown aria-hidden="true" className="h-3 w-3 shrink-0 text-muted" />
      </button>

      {/*
        The one polite status region of the switch (direction 6.3). It announces a switch or a renew
        while it runs, then its outcome: switched, refused, cancelled, failed. The ended elevation
        speaks in `ElevationNotice`. Always mounted, so a change is announced.
      */}
      <p role="status" aria-live="polite" data-testid="user-menu-status" className="sr-only">
        {status ?? ''}
      </p>

      {/*
        ⛔ CONDITIONALLY MOUNTED, not rendered-and-`hidden`. A hidden-but-present menu is found by
        `getByTestId` — which ignores `hidden` — so every "can the operator reach this" assertion
        would pass against a menu nobody can open. Unmounting makes the closed state observable by
        role queries, which respect it.

        ⛔ NO ENTRANCE ANIMATION, deliberately — so there is nothing for `prefers-reduced-motion` to
        gate. A menu is a response to a press, and Doherty puts the bar at "instant". The one motion
        inside it is the spinner of a running switch, which `motion-safe:` gates.
      */}
      {open && (
        <div className="absolute right-0 top-full z-40 mt-1 w-80 rounded-lg border border-border-subtle bg-surface-elevated">
          {/*
            The identity block sits OUTSIDE `role="menu"`. ⛔ Not a stylistic split: the children of a
            `menu` must be `menuitem`/`menuitemradio`/`menuitemcheckbox`/`separator`/`group`, and a
            block of static text inside one is announced as an item you cannot activate.
          */}
          <div className="px-3 pb-2 pt-2.5">
            <p className="truncate text-sm font-medium text-primary" title={email ?? undefined}>
              {label}
            </p>
            {active === null ? (
              /*
                No active-role claim (a union token, R-027): the held roles from the token, as name chips.
                ⛔ Rendered only when non-empty, and NOT replaced by "No roles" when empty:
                `ZeroAccessPanel` diagnoses the empty case with what to do about it.

                ⛔ A `ul`/`li`, NOT a `span` of `span`s with an `aria-label`. `aria-label` on a generic
                element is IGNORED by assistive technology and flagged by axe. `role="list"` supports a
                name, so the label works and the count is announced for free.
              */
              siteRoles.length > 0 && (
                <ul data-testid="user-menu-roles" aria-label="Your site roles" className="mt-1.5 flex flex-wrap gap-1">
                  {siteRoles.map((claimed, index) => {
                    // The claim is unchecked: an item that is not a string (null, an object) is an unnamed role.
                    const role = typeof claimed === 'string' ? claimed : '';
                    const id = role === '' ? `unnamed-${index}` : role;
                    return (
                      <li key={id} className="flex">
                        <RoleChip role={role} elevated={false} testId={`user-menu-role-${id}`} />
                      </li>
                    );
                  })}
                </ul>
              )
            ) : showSegments ? null : active.activeRole === null ? (
              <p data-testid="user-menu-no-role" className="mt-1.5 text-xs text-secondary">
                No site role.
              </p>
            ) : (
              // One held role, and it is active: the chip alone, with no segments.
              <div className="mt-2 flex">
                <RoleChip role={active.activeRole} elevated={elevated} testId="user-menu-active-role" />
              </div>
            )}
            {status === null ? null : (
              <p data-testid="user-menu-status-text" className="mt-1.5 text-xs text-secondary">
                {status}
              </p>
            )}
          </div>

          <div
            ref={menuRef}
            id={MENU_ID}
            role="menu"
            aria-label="Account"
            onKeyDown={onMenuKeyDown}
            data-testid="user-menu"
            className="pb-1"
          >
            {showSegments && (
              <div role="group" aria-label="Act as" data-testid="user-menu-act-as" className="px-3 pb-2">
                <div className="flex h-8 gap-0.5 rounded-lg border border-border-control p-0.5">
                  {options.map((role) => {
                    const index = nextIndex();
                    const isActive = role === active.activeRole;
                    const moving = switchingTo !== undefined && switchingTo === role;
                    const disabled = switchingTo !== undefined && !moving;
                    const icon = moving ? (
                      <Loader2 aria-hidden="true" className="h-3.5 w-3.5 shrink-0 motion-safe:animate-spin" />
                    ) : isActive && elevated ? (
                      <ShieldCheck aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
                    ) : !isActive && role === ELEVATED_ROLE ? (
                      <KeyRound aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
                    ) : null;
                    const hint = moving ? 'Switching…' : !isActive && role === ELEVATED_ROLE ? ELEVATION_HINT : null;
                    return (
                      <MenuRow
                        key={role}
                        role="menuitemradio"
                        variant="segment"
                        checked={isActive}
                        accent={isActive && elevated}
                        disabled={disabled}
                        focusable={roving === index}
                        onClick={() => {
                          setRoving(index);
                          choose(active, role);
                        }}
                        title={roleFullName(role)}
                        testId={`user-menu-act-as-${role}`}
                      >
                        {icon}
                        <span className="truncate">{roleName(role)}</span>
                        {hint === null ? null : <span className="sr-only">{`, ${hint}`}</span>}
                      </MenuRow>
                    );
                  })}
                </div>
              </div>
            )}

            {elevated && (
              <div role="none" className="flex h-8 items-center justify-between pl-3 pr-1 text-xs text-secondary">
                {/* The end in short; the full sentence names the rest role for hover and for a screen reader. */}
                <p data-testid="user-menu-elevation-line" title={elevationSentence(active.activeRole, elevatedUntilMs, active.restRole)}>
                  <span aria-hidden="true">Until {clockOf(elevatedUntilMs)}</span>
                  <span id={ELEVATION_SENTENCE_ID} data-testid="user-menu-elevation-sentence" className="sr-only">
                    {elevationSentence(active.activeRole, elevatedUntilMs, active.restRole)}
                  </span>
                </p>
                <Button
                  tone="ghost"
                  size="sm"
                  role="menuitem"
                  tabIndex={roving === nextIndex() ? 0 : -1}
                  aria-describedby={ELEVATION_SENTENCE_ID}
                  onClick={() => renew(active)}
                  data-testid="user-menu-renew"
                >
                  <RotateCw aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
                  Renew
                </Button>
              </div>
            )}

            <MenuSeparator />

            <MenuRow role="menuitem" focusable={roving === nextIndex()} onClick={openPasskeys} testId="user-menu-passkeys">
              <KeyRound aria-hidden="true" className="h-4 w-4 shrink-0" />
              Passkeys
            </MenuRow>

            <MenuSeparator />

            {/*
              R2/N3 — before a sign-out existed in this header at all, the only one was inside
              `ZeroAccessPanel`. ⛔ The testid is deliberately unchanged from when this was a
              top-level button.
            */}
            <MenuRow role="menuitem" danger focusable={roving === nextIndex()} onClick={onSignOut} testId="section-header-sign-out">
              <LogOut aria-hidden="true" className="h-4 w-4 shrink-0" />
              Sign out
            </MenuRow>
          </div>
        </div>
      )}

      {passkeysOpen && <PasskeyDialog onClose={closePasskeys} />}
    </div>
  );
}

/** The id of the full elevation sentence, which describes the Renew item. */
const ELEVATION_SENTENCE_ID = 'user-menu-elevation-sentence';

/** The full elevation sentence: "Admin until 18:36, then Developer." With no rest role it ends at the clock. */
export function elevationSentence(activeRole: string | null, untilMs: number, restRole: string | null): string {
  const end = `${elevationName(activeRole)} until ${clockOf(untilMs)}`;
  return restRole === null ? `${end}.` : `${end}, then ${actingAsText(restRole)}.`;
}

/**
 * The role chip: the WARD `Badge` in a `data-ward` scope, 28 px high, with the short name and the
 * full name on hover. Elevated, it takes the accent fill and a shield; at rest, the outline.
 */
function RoleChip({
  role,
  elevated,
  testId,
  shieldTestId,
}: {
  readonly role: string;
  readonly elevated: boolean;
  readonly testId: string;
  readonly shieldTestId?: string;
}): React.JSX.Element {
  return (
    <span data-ward="" className="contents">
      <Badge
        variant={elevated ? 'default' : 'outline'}
        title={roleFullName(role)}
        data-testid={testId}
        className="h-7 gap-1 whitespace-nowrap px-2.5 text-xs font-medium"
      >
        {elevated && <ShieldCheck aria-hidden="true" data-testid={shieldTestId} className="h-3.5 w-3.5 shrink-0" />}
        {roleName(role)}
      </Badge>
    </span>
  );
}

/** The rule between two parts of the account menu: one `separator`, not focusable. */
function MenuSeparator(): React.JSX.Element {
  return <div role="separator" className="my-1 border-t border-border-subtle"></div>;
}

/** The classes of a menu row: full width, 32 px high, left-aligned, with the focus ring inside the panel. */
// A held row keeps its text colour: an opacity on muted text drops it below 4.5:1 (WCAG AA).
const MENU_ROW_CLASS =
  'flex h-8 w-full items-center gap-2 px-3 text-left text-sm transition-colors focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus-ring aria-disabled:cursor-not-allowed';

/** The classes of a role segment: its content width plus a share of the track, 28 px high. */
const SEGMENT_CLASS =
  'flex h-7 min-w-0 flex-auto items-center justify-center gap-1.5 rounded-md px-2 text-xs font-medium transition-colors focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus-ring aria-disabled:cursor-not-allowed';

/** The fill of a segment: the accent for the active elevated role, the muted surface for another active role. */
function segmentToneClass(checked: boolean, accent: boolean): string {
  if (accent) return 'bg-accent text-on-accent';
  // A disabled segment keeps its look on hover, so it does not read as available.
  return checked
    ? 'bg-surface-muted text-primary'
    : 'text-secondary hover:bg-surface-muted hover:text-primary aria-disabled:hover:bg-transparent aria-disabled:hover:text-secondary';
}

/**
 * One row of the account menu: a `menuitem` or a `menuitemradio`, with the roving tab index.
 * `Button` cannot express a full-width, left-aligned menu row (its class is not forwardable), so the
 * menu's rows share this one element. `danger` paints Sign out in the critical tone, as before.
 * The `segment` variant is one role in the segment track.
 */
function MenuRow({
  role,
  variant = 'row',
  checked = false,
  accent = false,
  disabled = false,
  danger = false,
  focusable,
  onClick,
  title,
  testId,
  children,
}: {
  readonly role: 'menuitem' | 'menuitemradio';
  readonly variant?: 'row' | 'segment';
  /** `aria-checked`, for a `menuitemradio` only. */
  readonly checked?: boolean;
  /** The accent fill of the active elevated role, for a segment only. */
  readonly accent?: boolean;
  /** `aria-disabled` while another switch runs; the row stays focusable. */
  readonly disabled?: boolean;
  readonly danger?: boolean;
  /** `true` gives the row `tabIndex` 0, the roving stop; every other row has -1. */
  readonly focusable: boolean;
  readonly onClick: () => void;
  readonly title?: string;
  readonly testId: string;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  const className =
    variant === 'segment'
      ? `${SEGMENT_CLASS} ${segmentToneClass(checked, accent)}`
      : `${MENU_ROW_CLASS} ${
          danger ? 'text-state-critical hover:bg-state-critical/10' : 'text-secondary hover:bg-surface-muted hover:text-primary'
        }`;
  return (
    <button
      type="button"
      role={role}
      aria-checked={role === 'menuitemradio' ? checked : undefined}
      aria-disabled={disabled || undefined}
      tabIndex={focusable ? 0 : -1}
      onClick={onClick}
      title={title}
      data-testid={testId}
      className={className}
    >
      {children}
    </button>
  );
}
